import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { ArrowLeft, Edit2, ChevronDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useEmployee, useUpdateEmployeeStatus, useTerminateEmployee } from '@/api/queries/employee.queries';
import { selectCompanyName } from '@/store/slices/companySlice';
import { useEmployeeAttendance } from '@/api/queries/attendance.queries';
import { useLeaveRequests, useLeaveBalances } from '@/api/queries/leave.queries';
import { usePayrollRuns, usePayrollRun } from '@/api/queries/payroll.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/cn';
import { EMPLOYEE_FIELD_REGISTRY, FIELD_CATEGORIES } from '@/lib/employeeFields';
import { DocumentsSection } from '@/components/employees/DocumentsSection';
import { useCompanySettings } from '@/api/queries/settings.queries';
import { useIssuedDocuments, useMaterials, useAcknowledgeDocument } from '@/api/queries/document.queries';
import { FileText, Package, CheckCircle2, X as XIcon } from 'lucide-react';

const TABS = ['Overview','Attendance','Payroll','Leave','Documents'];

function InfoRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between text-[13px] gap-3">
      <span className="text-[var(--text-secondary)] shrink-0">{label}</span>
      <span className="text-[var(--text-primary)] font-medium text-right">{String(value)}</span>
    </div>
  );
}

/** Renders the populated extended_details fields for a category, skipping
 *  empty values, object/repeatable fields (shown as their own cards below)
 *  and anything the company hasn't enabled / never filled in. */
function ExtendedCategoryCard({ title, fields, extended }) {
  const rows = fields.filter(f => f.target === 'extended' && f.type !== 'object' && f.type !== 'repeatable');
  const populated = rows.filter(f => extended?.[f.key] !== undefined && extended?.[f.key] !== null && extended?.[f.key] !== '');
  if (!populated.length) return null;
  return (
    <div className="card space-y-3 p-5">
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{title}</h3>
      {populated.map(f => <InfoRow key={f.key} label={f.label} value={f.type === 'enum' ? extended[f.key]?.replace(/_/g,' ') : f.type === 'boolean' ? (extended[f.key] ? 'Yes' : 'No') : extended[f.key]}/>)}
    </div>
  );
}

function NomineeFamilyCard({ extended }) {
  const nominee = extended?.nominee;
  const family = extended?.family_members || [];
  const hasNominee = nominee && Object.values(nominee).some(v => v);
  if (!hasNominee && !family.length) return null;
  return (
    <div className="card space-y-3 p-5 sm:col-span-2">
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Nominee & Family</h3>
      {hasNominee && (
        <div className="rounded-[10px] bg-[var(--bg-muted)] p-3 space-y-1">
          <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-1">Nominee</p>
          <InfoRow label="Name" value={nominee.name}/>
          <InfoRow label="Relation" value={nominee.relation}/>
          <InfoRow label="DOB" value={nominee.dob}/>
          <InfoRow label="Aadhaar" value={nominee.aadhaar_number}/>
        </div>
      )}
      {family.length > 0 && (
        <div className="space-y-2">
          {family.map((m, i) => (
            <div key={i} className="rounded-[10px] bg-[var(--bg-muted)] p-3 space-y-1">
              <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-1">Family Member {i+1}</p>
              <InfoRow label="Name" value={m.name}/>
              <InfoRow label="Relation" value={m.relation}/>
              <InfoRow label="DOB" value={m.dob}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ emp }) {
  const extended = emp.extended_details || {};
  const { data: settingsResp } = useCompanySettings();
  const customFields = settingsResp?.data?.settings?.custom_employee_fields || [];
  const customValues = extended.custom || {};
  const companyName = useSelector(selectCompanyName);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="card space-y-3 p-5">
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Personal</h3>
        {[['Email',emp.email],['Phone',emp.phone],['Joining Date',emp.joining_date],['Employee Code',emp.employee_code]].map(([l,v])=>(
          <div key={l} className="flex justify-between text-[13px]">
            <span className="text-[var(--text-secondary)]">{l}</span>
            <span className="text-[var(--text-primary)] font-medium">{v}</span>
          </div>
        ))}
      </div>
      <div className="card space-y-3 p-5">
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Work</h3>
        {[
          ['Company', companyName],
          ['Department', emp.department?.name || (typeof emp.department === 'string' ? emp.department : '—')],
          ['Designation', emp.designation],
          ['Employment Type', emp.employment_type?.replace(/_/g,' ')],
          ['Manager', emp.reporting_manager?.name || emp.reporting_manager || '—']
        ].map(([l,v]) => (
          <div key={l} className="flex justify-between text-[13px]">
            <span className="text-[var(--text-secondary)]">{l}</span>
            <span className="text-[var(--text-primary)] font-medium">{v}</span>
          </div>
        ))}
      </div>
      <div className="card space-y-3 p-5">
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Compensation</h3>
        {[['Annual CTC',formatCurrency(emp.salary?.ctc_annual||0)],['Monthly Gross',formatCurrency(emp.salary?.gross_monthly||0)],['Basic',formatCurrency(emp.salary?.basic||0)]].map(([l,v])=>(
          <div key={l} className="flex justify-between text-[13px]">
            <span className="text-[var(--text-secondary)]">{l}</span>
            <span className="text-[var(--text-primary)] font-medium">{v}</span>
          </div>
        ))}
      </div>
      <div className="card space-y-3 p-5">
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Bank Details</h3>
        <InfoRow label="Bank Name" value={emp.bank_name}/>
        <InfoRow label="Account Holder Name" value={emp.account_holder_name}/>
        <InfoRow label="Account Number" value={emp.account_number}/>
        <InfoRow label="IFSC Code" value={emp.ifsc_code}/>
        <InfoRow label="Account Owner" value={emp.extended_details?.is_own_bank_account === false ? "Someone else's account" : 'Employee’s own account'}/>
      </div>
      <div className="card space-y-3 p-5">
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Today</h3>
        {emp.today_attendance ? (
          <>
            <div className="flex justify-between text-[13px]"><span className="text-[var(--text-secondary)]">Status</span><StatusBadge status={emp.today_attendance.status}/></div>
            <div className="flex justify-between text-[13px]"><span className="text-[var(--text-secondary)]">Check In</span><span className="text-[var(--text-primary)] font-medium">{emp.today_attendance.check_in||'—'}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-[var(--text-secondary)]">Check Out</span><span className="text-[var(--text-primary)] font-medium">{emp.today_attendance.check_out||'—'}</span></div>
          </>
        ) : <p className="text-[13px] text-[var(--text-tertiary)]">No attendance record</p>}
      </div>

      {/* Extended fields — only render cards that have at least one
          populated value, so a company that hasn't opted into a category
          (or an employee with no data in it) sees no empty card. */}
      {FIELD_CATEGORIES.filter(c => !['personal','employment','bank_salary','nominee_family'].includes(c.key)).map(cat => (
        <ExtendedCategoryCard key={cat.key} title={cat.label}
          fields={EMPLOYEE_FIELD_REGISTRY.filter(f => f.category === cat.key)} extended={extended}/>
      ))}
      <ExtendedCategoryCard title="Personal Details" fields={EMPLOYEE_FIELD_REGISTRY.filter(f => f.category === 'personal')} extended={extended}/>
      <NomineeFamilyCard extended={extended}/>

      {/* Custom fields — company-defined */}
      {customFields.length > 0 && (
        <div className="card space-y-3 p-5 sm:col-span-2">
          <div className="flex items-center gap-2">
            <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Custom Fields</h3>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] flex items-center gap-0.5">
              ⚡ Your Fields
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {customFields.map(f => {
              const val = customValues[f.key];
              if (val === undefined || val === null || val === '') return null;
              return (
                <div key={f.key} className="flex justify-between text-[13px]">
                  <span className="text-[var(--text-secondary)]">{f.label}</span>
                  <span className="text-[var(--text-primary)] font-medium">
                    {f.type === 'checkbox' ? (val ? 'Yes' : 'No') :
                     f.type === 'photo' ? <a href={val} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] hover:underline">View</a> :
                     String(val)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AttendanceTab({ empId }) {
  const { data: attendResp } = useEmployeeAttendance(empId);
  const records = attendResp?.data || [];
  if (!records.length) return <div className="card text-center py-10 text-[14px] text-[var(--text-tertiary)]">No attendance records found</div>;
  return (
    <div className="card p-0 overflow-x-auto">
      <table className="w-full">
        <thead><tr className="border-b border-[var(--border)]">{['Date','Check In','Check Out','Hours','Status'].map(h=><th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-[var(--border)]">
          {records.slice(0,15).map((r,i)=>(
            <tr key={r.date||i} className="hover:bg-[var(--bg-muted)]">
              <td className="px-5 py-3 text-[13px] text-[var(--text-primary)]">{r.date}</td>
              <td className="px-5 py-3 text-[13px] text-[var(--text-secondary)]">{r.check_in||'—'}</td>
              <td className="px-5 py-3 text-[13px] text-[var(--text-secondary)]">{r.check_out||'—'}</td>
              <td className="px-5 py-3 text-[13px] text-[var(--text-secondary)]">{r.hours_worked||'—'}</td>
              <td className="px-5 py-3"><StatusBadge status={r.status}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayrollTab({ empId }) {
  const { data: runsResp } = usePayrollRuns();
  const latestId = runsResp?.data?.[0]?.id;
  const { data: runResp } = usePayrollRun(latestId);
  const items = (runResp?.data?.items || []).filter(i => i.employee?.id === empId);
  if (!items.length) return <div className="card text-center py-10 text-[14px] text-[var(--text-tertiary)]">No payroll records</div>;
  return (
    <div className="space-y-3">
      {items.map(item=>(
        <div key={item.id} className="card grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">Gross</p><p className="text-[16px] font-bold text-[var(--text-primary)]">{formatCurrency(item.gross_salary)}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">Net Pay</p><p className="text-[16px] font-bold text-[var(--success)]">{formatCurrency(item.net_pay)}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">PF</p><p className="text-[14px] text-[var(--text-secondary)]">{formatCurrency(item.pf_employee)}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">TDS</p><p className="text-[14px] text-[var(--text-secondary)]">{formatCurrency(item.tds)}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">Present Days</p><p className="text-[14px] text-[var(--text-secondary)]">{item.present_days}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">LOP Days</p><p className="text-[14px] text-[var(--text-secondary)]">{item.lop_days}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">PT</p><p className="text-[14px] text-[var(--text-secondary)]">{formatCurrency(item.pt)}</p></div>
          <div><p className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">Status</p><StatusBadge status={item.payment_status}/></div>
        </div>
      ))}
    </div>
  );
}

function LeaveTab({ empId }) {
  const { data: requestsResp } = useLeaveRequests({ employee_id: empId });
  const requests = requestsResp?.data || [];

  const { data: balancesResp } = useLeaveBalances(empId);
  const balances = balancesResp?.data || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {balances.map(b=>(
          <div key={b.type} className="card text-center p-5">
            <p className="text-[24px] font-black text-[var(--text-primary)]">{b.available}</p>
            <p className="text-[12px] text-[var(--text-secondary)] font-medium">{b.type}</p>
            <p className="text-[11px] text-[var(--text-tertiary)]">{b.used} used of {b.total}</p>
          </div>
        ))}
      </div>
      {requests.length > 0 ? (
        <div className="card p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--border)]"><p className="text-[13px] font-semibold text-[var(--text-primary)]">Leave Requests</p></div>
          <div className="divide-y divide-[var(--border)]">
            {requests.map(r=>(
              <div key={r.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-[13px] font-medium text-[var(--text-primary)]">{r.leave_type}</p>
                  <p className="text-[11px] text-[var(--text-tertiary)]">{r.from_date} → {r.to_date} ({r.days}d)</p>
                </div>
                <StatusBadge status={r.status}/>
              </div>
            ))}
          </div>
        </div>
      ) : <div className="card text-center py-8 text-[14px] text-[var(--text-tertiary)]">No leave requests</div>}
    </div>
  );
}

function StatusDropdown({ emp }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const updateStatus = useUpdateEmployeeStatus();
  const terminateEmployee = useTerminateEmployee();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Active', color: 'var(--success)' },
    { value: 'INACTIVE', label: 'Inactive', color: 'var(--warning)' },
    { value: 'ON_LEAVE', label: 'On Leave', color: 'var(--accent)' },
    { value: 'TERMINATED', label: 'Terminated', color: 'var(--danger)', destructive: true },
  ];

  const handleStatus = (opt) => {
    setOpen(false);
    if (opt.value === emp.status) return;
    if (opt.destructive) {
      if (!confirm(`Terminate ${emp.first_name} ${emp.last_name}? This marks them as terminated.`)) return;
      terminateEmployee.mutate({ id: emp.id, reason: 'Manual termination' }, {
        onSuccess: () => toast.success(`${emp.first_name} ${emp.last_name} terminated`),
        onError: () => toast.error('Failed to terminate employee'),
      });
    } else {
      updateStatus.mutate({ id: emp.id, status: opt.value }, {
        onSuccess: () => toast.success(`Status changed to ${opt.label}`),
        onError: () => toast.error('Failed to update status'),
      });
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] border border-[var(--border)] hover:bg-[var(--bg-muted)] transition-colors group">
        <StatusBadge status={emp.status}/>
        <ChevronDown size={12} className="text-[var(--text-tertiary)] group-hover:text-[var(--text-secondary)]"/>
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 w-44 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] shadow-[var(--shadow-lg)] overflow-hidden fade-in">
          {STATUS_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => handleStatus(opt)}
              className={cn('w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left hover:bg-[var(--bg-muted)] transition-colors',
                opt.value === emp.status && 'bg-[var(--bg-muted)]',
                opt.destructive && 'text-[var(--danger)]'
              )}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: opt.color }}/>
              {opt.label}
              {opt.value === emp.status && <span className="ml-auto text-[10px] font-bold text-[var(--text-tertiary)]">current</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Documents Tab ────────────────────────────────────────────────────────────

function IssuedDocModal({ doc, onClose, onAcknowledge }) {
  if (!doc) return null;
  const isAcked = doc.status === 'ACKNOWLEDGED';
  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${doc.template_name || 'Document'}</title></head><body>${doc.content_html || doc.content || '<p>No content available.</p>'}</body></html>`);
    win.document.close();
    win.print();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-[560px] bg-[var(--bg-card)] rounded-[16px] border border-[var(--border)] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-bold text-[var(--text-primary)]">{doc.document_name || doc.template_name || 'Document'}</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-[6px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]"><XIcon size={14}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <div>
              <span className="text-[var(--text-tertiary)]">Issued Date</span>
              <p className="font-medium text-[var(--text-primary)] mt-0.5">{doc.issued_at?.slice(0,10) || doc.issued_date || '—'}</p>
            </div>
            <div>
              <span className="text-[var(--text-tertiary)]">Status</span>
              <p className={`font-semibold mt-0.5 ${isAcked ? 'text-emerald-600' : doc.status === 'DRAFT' ? 'text-gray-500' : 'text-amber-600'}`}>
                {isAcked ? 'Acknowledged' : doc.status === 'DRAFT' ? 'Draft' : 'Pending Acknowledgement'}
              </p>
            </div>
            {doc.category && <div><span className="text-[var(--text-tertiary)]">Category</span><p className="font-medium text-[var(--text-primary)] mt-0.5">{doc.category}</p></div>}
            {doc.issued_by && <div><span className="text-[var(--text-tertiary)]">Issued By</span><p className="font-medium text-[var(--text-primary)] mt-0.5">{doc.issued_by}</p></div>}
          </div>
          {(doc.content_html || doc.content) && (
            <div className="rounded-[10px] border border-[var(--border)] overflow-hidden">
              <div className="px-3 py-1.5 bg-[var(--bg-muted)] border-b border-[var(--border)]">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Document Content</p>
              </div>
              <div className="max-h-64 overflow-y-auto p-3 bg-white dark:bg-[var(--bg-muted)]">
                {doc.content_html
                  ? <div className="text-[12px]" dangerouslySetInnerHTML={{ __html: doc.content_html }} />
                  : <p className="text-[12px] text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">{doc.content}</p>
                }
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <button onClick={handlePrint}
              className="flex items-center justify-center gap-1.5 py-2 rounded-[8px] border border-[var(--border)] text-[12px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] transition-colors">
              🖨 Print / PDF
            </button>
            {!isAcked ? (
              <button onClick={() => onAcknowledge(doc.id)}
                className="flex items-center justify-center gap-1.5 py-2 rounded-[8px] bg-emerald-500 text-white text-[12px] font-semibold hover:bg-emerald-600 transition-colors col-span-1">
                <CheckCircle2 size={13}/> Ack by HR
              </button>
            ) : (
              <div className="flex items-center justify-center gap-1.5 py-2 rounded-[8px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-[12px] font-semibold col-span-1">
                <CheckCircle2 size={13}/> HR Acknowledged
              </div>
            )}
            <button disabled
              className="flex items-center justify-center gap-1.5 py-2 rounded-[8px] border border-dashed border-[var(--border)] text-[12px] font-medium text-[var(--text-tertiary)] opacity-60 cursor-not-allowed">
              <CheckCircle2 size={13}/> Ack by Employee
              <span className="text-[9px]">(Staff App)</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MaterialDetailModal({ mat, onClose }) {
  if (!mat) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-[440px] bg-[var(--bg-card)] rounded-[16px] border border-[var(--border)] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-bold text-[var(--text-primary)]">{mat.material_name || mat.item_name || 'Material'}</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-[6px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]"><XIcon size={14}/></button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-3 text-[12px]">
          {[
            ['Category', mat.category],
            ['Asset Code', mat.asset_code],
            ['Serial Number', mat.serial_number],
            ['Condition', mat.condition],
            ['Issue Date', mat.issue_date],
            ['Expected Return', mat.expected_return || '—'],
            ['Cost Value', mat.cost_value ? `₹${Number(mat.cost_value).toLocaleString('en-IN')}` : '—'],
            ['Status', mat.status],
          ].map(([label, value]) => (
            <div key={label}>
              <span className="text-[var(--text-tertiary)]">{label}</span>
              <p className="font-medium text-[var(--text-primary)] mt-0.5">{value || '—'}</p>
            </div>
          ))}
          {mat.notes && (
            <div className="col-span-2">
              <span className="text-[var(--text-tertiary)]">Notes</span>
              <p className="font-medium text-[var(--text-primary)] mt-0.5">{mat.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ emp, navigate }) {
  const [viewDoc, setViewDoc] = useState(null);
  const [viewMaterial, setViewMaterial] = useState(null);
  const { data: issuedResp } = useIssuedDocuments({ employee_id: emp.id });
  const { data: materialsResp } = useMaterials({ employee_id: emp.id });
  const acknowledge = useAcknowledgeDocument();

  const issuedDocs = issuedResp?.data || [];
  const materials = materialsResp?.data || [];

  return (
    <div className="space-y-4">
      {/* Original documents section */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-bold text-[var(--text-primary)]">Documents & Files</h3>
          <Button size="sm" variant="outline" onClick={() => navigate(`/employees/${emp.id}/edit`)}>Upload / Edit</Button>
        </div>
        <DocumentsSection docs={emp.extended_details?.documents || []} viewOnly/>
      </div>

      {/* Issued Documents section */}
      <div className="card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FileText size={15} className="text-[var(--accent)]"/>
          <h3 className="text-[13px] font-bold text-[var(--text-primary)]">Issued Documents</h3>
          {issuedDocs.length > 0 && <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)]">{issuedDocs.length}</span>}
        </div>
        {issuedDocs.length === 0 ? (
          <p className="text-[12px] text-[var(--text-tertiary)] py-3 text-center">No documents issued to this employee yet</p>
        ) : (
          <div className="divide-y divide-[var(--border)] rounded-[10px] border border-[var(--border)] overflow-hidden">
            {issuedDocs.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-muted)] transition-colors">
                <FileText size={14} className="text-[var(--text-tertiary)] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[var(--text-primary)] truncate">{doc.document_name || doc.template_name || 'Document'}</p>
                  <p className="text-[11px] text-[var(--text-tertiary)]">{doc.issued_at?.slice(0,10) || doc.issued_date || '—'}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${doc.status === 'ACKNOWLEDGED' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {doc.status === 'ACKNOWLEDGED' ? 'Acknowledged' : 'Pending'}
                </span>
                <button onClick={() => setViewDoc(doc)}
                  className="text-[11px] font-medium text-[var(--accent)] hover:underline shrink-0">
                  View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Issued Materials section */}
      <div className="card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Package size={15} className="text-purple-500"/>
          <h3 className="text-[13px] font-bold text-[var(--text-primary)]">Issued Materials</h3>
          {materials.length > 0 && <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{materials.length}</span>}
        </div>
        {materials.length === 0 ? (
          <p className="text-[12px] text-[var(--text-tertiary)] py-3 text-center">No materials issued to this employee yet</p>
        ) : (
          <div className="divide-y divide-[var(--border)] rounded-[10px] border border-[var(--border)] overflow-hidden">
            <div className="grid grid-cols-5 gap-2 px-4 py-2 bg-[var(--bg-muted)] text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
              <span className="col-span-2">Item</span><span>Issued</span><span>Return By</span><span>Status</span>
            </div>
            {materials.map(mat => (
              <div key={mat.id} className="grid grid-cols-5 gap-2 px-4 py-3 text-[12px] hover:bg-[var(--bg-muted)] transition-colors items-center">
                <div className="col-span-2">
                  <p className="font-medium text-[var(--text-primary)] truncate">{mat.material_name || mat.item_name || '—'}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)]">{mat.serial_number || mat.asset_code || ''}</p>
                </div>
                <span className="text-[var(--text-secondary)]">{mat.issue_date?.slice(0,10) || mat.issued_date?.slice(0,10) || '—'}</span>
                <span className="text-[var(--text-secondary)]">{mat.expected_return?.slice(0,10) || mat.return_date?.slice(0,10) || '—'}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${mat.status === 'RETURNED' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                    {mat.status || 'Active'}
                  </span>
                  <button onClick={() => setViewMaterial(mat)} className="text-[11px] font-medium text-[var(--accent)] hover:underline shrink-0">View</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Document Modal */}
      {viewDoc && (
        <IssuedDocModal
          doc={viewDoc}
          onClose={() => setViewDoc(null)}
          onAcknowledge={(id) => {
            acknowledge.mutate(id, { onSuccess: () => setViewDoc(null) });
          }}
        />
      )}
      {/* View Material Modal */}
      {viewMaterial && <MaterialDetailModal mat={viewMaterial} onClose={() => setViewMaterial(null)} />}
    </div>
  );
}

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Overview');

  const { data: empResp, isLoading } = useEmployee(id);
  const emp = empResp?.data;

  if (isLoading) return <div className="card text-center py-16 text-[14px] text-[var(--text-tertiary)]">Loading…</div>;
  if (!emp) return (
    <div className="card text-center py-16">
      <p className="text-[14px] text-[var(--text-secondary)]">Employee not found</p>
      <Button variant="ghost" onClick={()=>navigate('/employees')} className="mt-3">Back to Employees</Button>
    </div>
  );

  return (
    <div className="space-y-5 fade-in">
      {/* Terminated banner */}
      {emp.status === 'TERMINATED' && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-[12px] bg-[var(--danger)]/10 border border-[var(--danger)]/30 text-[var(--danger)] text-[13px] font-medium">
          <AlertTriangle size={14} className="shrink-0"/>
          This employee has been terminated and is no longer active.
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={()=>navigate('/employees')} className="p-2 hover:bg-[var(--bg-muted)] rounded-[8px] transition-colors">
          <ArrowLeft size={18} className="text-[var(--text-secondary)]"/>
        </button>
        <div className="flex items-center gap-4 flex-1">
          <Avatar name={`${emp.first_name} ${emp.last_name}`} src={emp.photo_url} size="xl"/>
          <div>
            <h1 className={cn('text-[20px] font-bold text-[var(--text-primary)]', emp.status === 'TERMINATED' && 'line-through opacity-70')}>{emp.first_name} {emp.last_name}</h1>
            <p className="text-[13px] text-[var(--text-secondary)]">{emp.designation} · {emp.department?.name || (typeof emp.department === 'string' ? emp.department : '')}</p>
            <div className="flex items-center gap-2 mt-1">
              <StatusDropdown emp={emp}/>
              <span className="text-[11px] text-[var(--text-tertiary)]">{emp.employee_code}</span>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" icon={Edit2} onClick={()=>navigate(`/employees/${id}/edit`)}>Edit</Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--border)] overflow-x-auto">
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            className={cn('px-4 py-2.5 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors',
              tab===t ? 'border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            )}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab==='Overview' && <OverviewTab emp={emp}/>}
      {tab==='Attendance' && <AttendanceTab empId={emp.id}/>}
      {tab==='Payroll' && <PayrollTab empId={emp.id}/>}
      {tab==='Leave' && <LeaveTab empId={emp.id}/>}
      {tab==='Documents' && <DocumentsTab emp={emp} navigate={navigate} />}
    </div>
  );
}
